import { useState, useEffect } from "react";
import axios from "axios";

// ✅ Fixed exchange rates (TO USD conversion)
const FIXED_RATES = {
  USD: 1,
  EUR: 1.08, // 1 EUR = 1.08 USD
  GBP: 1.27, // 1 GBP = 1.27 USD
  CAD: 0.72, // 1 CAD = 0.72 USD
  AUD: 0.65, // 1 AUD = 0.65 USD
  JPY: 0.0067, // 1 JPY = 0.0067 USD
  CNY: 0.14, // 1 CNY = 0.14 USD
  INR: 0.012, // 1 INR = 0.012 USD
  BRL: 0.2, // 1 BRL = 0.20 USD
  MXN: 0.05, // 1 MXN = 0.050 USD
  KRW: 0.00075, // 1 KRW = 0.00075 USD
  RUB: 0.011, // 1 RUB = 0.011 USD
  TRY: 0.029, // 1 TRY = 0.029 USD
  CHF: 1.13, // 1 CHF = 1.13 USD
  SEK: 0.096, // 1 SEK = 0.096 USD
  NOK: 0.093, // 1 NOK = 0.093 USD
  DKK: 0.145, // 1 DKK = 0.145 USD
  PLN: 0.25, // 1 PLN = 0.25 USD
  NZD: 0.6, // 1 NZD = 0.60 USD
  ZAR: 0.055, // 1 ZAR = 0.055 USD
  SGD: 0.74, // 1 SGD = 0.74 USD
  HKD: 0.13, // 1 HKD = 0.13 USD
  THB: 0.029, // 1 THB = 0.029 USD
  MYR: 0.22, // 1 MYR = 0.22 USD
  PHP: 0.017, // 1 PHP = 0.017 USD
  IDR: 0.000063, // 1 IDR = 0.000063 USD
  VND: 0.000039, // 1 VND = 0.000039 USD
  AED: 0.27, // 1 AED = 0.27 USD
  SAR: 0.27, // 1 SAR = 0.27 USD
  ILS: 0.27, // 1 ILS = 0.27 USD
  CZK: 0.044, // 1 CZK = 0.044 USD
  HUF: 0.0028, // 1 HUF = 0.0028 USD
  ARS: 0.001, // 1 ARS = 0.0010 USD
  CLP: 0.0011, // 1 CLP = 0.0011 USD
  COP: 0.00023, // 1 COP = 0.00023 USD
  PEN: 0.26, // 1 PEN = 0.26 USD
  EGP: 0.02, // 1 EGP = 0.020 USD
  NGN: 0.00065, // 1 NGN = 0.00065 USD
  KES: 0.0077, // 1 KES = 0.0077 USD
  PKR: 0.0036, // 1 PKR = 0.0036 USD
  BDT: 0.0091, // 1 BDT = 0.0091 USD
  KZT: 0.002, // ✅ 1 KZT = 0.0020 USD (Kazakhstan Tenge)
};

/**
 * Hook to fetch live exchange rates or use fixed rates as fallback
 * @param {boolean} useLiveRates - Whether to fetch live rates (default: true)
 * @returns {object} { rates, loading, error, lastUpdated }
 */
export const useExchangeRates = (useLiveRates = true) => {
  const [rates, setRates] = useState(FIXED_RATES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (!useLiveRates) {
      setRates(FIXED_RATES);
      setLastUpdated(new Date());
      return;
    }

    const fetchRates = async () => {
      setLoading(true);
      setError(null);

      try {
        // ✅ Using exchangerate-api.com
        const response = await axios.get("https://api.exchangerate-api.com/v4/latest/USD");

        if (response.data && response.data.rates) {
          // ✅ INVERT the rates (API gives USD to X, we need X to USD)
          const invertedRates = {};

          Object.entries(response.data.rates).forEach(([currency, rate]) => {
            // Convert: if 1 USD = 0.92 EUR, then 1 EUR = 1/0.92 = 1.087 USD
            invertedRates[currency] = 1 / rate;
          });

          setRates(invertedRates);
          setLastUpdated(new Date());
        } else {
          throw new Error("Invalid API response");
        }
      } catch (err) {
        console.error("Failed to fetch exchange rates:", err);
        setError("Failed to fetch live rates. Using fixed rates.");
        setRates(FIXED_RATES);
        setLastUpdated(new Date());
      } finally {
        setLoading(false);
      }
    };

    fetchRates();

    // ✅ Refresh rates every 24 hours
    const interval = setInterval(fetchRates, 24 * 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [useLiveRates]);

  return { rates, loading, error, lastUpdated };
};
